'use client';

import React from 'react';
import {
  ShieldCheck,
  X,
  ChevronRight,
  Activity,
  FileText,
  Layers,
  Users,
  History,
  LogIn,
  KeyRound,
} from 'lucide-react';

interface SidebarProps {
  activeTab: 'OVERVIEW' | 'KYC' | 'WORKSPACES' | 'USERS' | 'AUDIT_LOG';
  setActiveTab: (tab: 'OVERVIEW' | 'KYC' | 'WORKSPACES' | 'USERS' | 'AUDIT_LOG') => void;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  kycCount: number;
  wsTotal: number;
  usersTotal: number;
  setUserFilter: (filter: 'ALL' | 'ORGANIZATION' | 'MEMBER' | 'SUSPENDED') => void;
  setUsersPage: (page: number) => void;
  onOpenChangePassword: () => void;
  onSignOut: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  activeTab,
  setActiveTab,
  mobileMenuOpen,
  setMobileMenuOpen,
  kycCount,
  wsTotal,
  usersTotal,
  setUserFilter,
  setUsersPage,
  onOpenChangePassword,
  onSignOut,
}) => {
  return (
    <aside
      className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0A0D1A] border-r border-slate-800/80 flex flex-col justify-between transition-transform duration-300 ease-in-out lg:static lg:translate-x-0 ${
        mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      }`}
    >
      <div className="p-5 space-y-6">
        {/* Logo & Brand */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-tr from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/25 text-white">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-sm font-black text-white tracking-tight">ScaleSutra</h2>
              <p className="text-[10px] text-indigo-400 font-mono font-bold uppercase tracking-wider">
                Superadmin Pro
              </p>
            </div>
          </div>

          <button
            onClick={() => setMobileMenuOpen(false)}
            className="lg:hidden text-slate-400 hover:text-white p-1 cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="space-y-1.5 pt-2">
          <p className="px-3 text-[10px] font-extrabold text-slate-500 uppercase tracking-widest mb-2">
            Platform Controls
          </p>

          <button
            onClick={() => {
              setActiveTab('OVERVIEW');
              setMobileMenuOpen(false);
            }}
            className={`w-full px-3.5 py-3 rounded-2xl text-xs font-bold flex items-center justify-between transition-all cursor-pointer ${
              activeTab === 'OVERVIEW'
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-600/25'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900/80'
            }`}
          >
            <div className="flex items-center gap-3">
              <Activity className="w-4 h-4" />
              <span>Overview</span>
            </div>
            <ChevronRight className="w-3.5 h-3.5 opacity-60" />
          </button>

          <button
            onClick={() => {
              setActiveTab('KYC');
              setMobileMenuOpen(false);
            }}
            className={`w-full px-3.5 py-3 rounded-2xl text-xs font-bold flex items-center justify-between transition-all cursor-pointer ${
              activeTab === 'KYC'
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-600/25'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900/80'
            }`}
          >
            <div className="flex items-center gap-3">
              <FileText className="w-4 h-4" />
              <span>KYC Verification</span>
            </div>
            <ChevronRight className="w-3.5 h-3.5 opacity-60" />
          </button>

          <button
            onClick={() => {
              setActiveTab('WORKSPACES');
              setMobileMenuOpen(false);
            }}
            className={`w-full px-3.5 py-3 rounded-2xl text-xs font-bold flex items-center justify-between transition-all cursor-pointer ${
              activeTab === 'WORKSPACES'
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-600/25'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900/80'
            }`}
          >
            <div className="flex items-center gap-3">
              <Layers className="w-4 h-4" />
              <span>Workspaces</span>
            </div>
            <ChevronRight className="w-3.5 h-3.5 opacity-60" />
          </button>

          <button
            onClick={() => {
              setActiveTab('USERS');
              setUserFilter('ALL');
              setUsersPage(1);
              setMobileMenuOpen(false);
            }}
            className={`w-full px-3.5 py-3 rounded-2xl text-xs font-bold flex items-center justify-between transition-all cursor-pointer ${
              activeTab === 'USERS'
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-600/25'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900/80'
            }`}
          >
            <div className="flex items-center gap-3">
              <Users className="w-4 h-4" />
              <span>User Directory</span>
            </div>
            <ChevronRight className="w-3.5 h-3.5 opacity-60" />
          </button>

          <button
            onClick={() => {
              setActiveTab('AUDIT_LOG');
              setMobileMenuOpen(false);
            }}
            className={`w-full px-3.5 py-3 rounded-2xl text-xs font-bold flex items-center justify-between transition-all cursor-pointer ${
              activeTab === 'AUDIT_LOG'
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-600/25'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-900/80'
            }`}
          >
            <div className="flex items-center gap-3">
              <History className="w-4 h-4" />
              <span>Audit Logs</span>
            </div>
            <ChevronRight className="w-3.5 h-3.5 opacity-60" />
          </button>
        </nav>
      </div>

      {/* User Profile Footer */}
      <div className="p-4 border-t border-slate-800/80 bg-[#070A14]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-indigo-600 to-purple-600 text-white flex items-center justify-center font-black text-xs shadow-md">
              SA
            </div>
            <div className="overflow-hidden">
              <p className="text-xs font-extrabold text-white truncate">Superadmin</p>
              <p className="text-[10px] text-slate-400 truncate">admin@cognoflux.com</p>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={onOpenChangePassword}
              className="text-slate-400 hover:text-indigo-400 p-1.5 transition-colors cursor-pointer"
              title="Change Password"
            >
              <KeyRound className="w-4 h-4" />
            </button>

            <button
              onClick={onSignOut}
              className="text-slate-400 hover:text-rose-400 p-1.5 transition-colors cursor-pointer"
              title="Sign Out"
            >
              <LogIn className="w-4 h-4 rotate-180" />
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
};
